import {
  Box,
  Button,
  Link as MuiLink,
  Stack,
  SvgIcon,
  Typography,
} from "@mui/material";
import * as Sentry from "@sentry/react";
import { useScreenWidth } from "@msys/ui";
import { T, useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import { Link, Navigate, useLocation } from "react-router-dom";
import { ORGANISATION_TYPE_URL_SEARCH_PARAM } from "../../constants.js";
import {
  ONBOARDING_COLORS,
  ONBOARDING_SMALL_TEXT_BUTTON,
} from "../../features/organisations/onboarding/onboardingDesign.js";
import type { CreateableOrganisationType } from "../isValidOrganisationType.js";
import { useAuth } from "../useAuth.js";
import { GateCollage } from "./GateCollage.js";
import { SignupSplitLayout } from "./SignupSplitLayout.js";

/** A line of body text followed by a small text button, as on `/signup`. */
const FOOTER_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexWrap: "wrap",
  columnGap: "4px",
  fontSize: 14,
  lineHeight: 1.45,
  letterSpacing: "normal",
  color: ONBOARDING_COLORS.body,
} as const;

/**
 * The entry for uninvited visitors on `/` (Karo's gate): two cards that open
 * `/signup` for the chosen role, keeping the landing URL's other parameters
 * (e.g. a referral token). A signed-in visitor goes straight into the app.
 */
export const SignupGatePage = () => {
  const { auth, isAuthenticated } = useAuth();
  const { t } = useTranslate(["PageSignup", "Global"]);
  const { search } = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { isMinTablet } = useScreenWidth();

  if (isAuthenticated) {
    return <Navigate to={{ pathname: "/next", search }} replace />;
  }

  const signupSearch = (organisationType: CreateableOrganisationType) => {
    const params = new URLSearchParams(search);
    params.set(ORGANISATION_TYPE_URL_SEARCH_PARAM, organisationType);
    return `?${params.toString()}`;
  };

  return (
    <SignupSplitLayout
      signupRole="GATE"
      contentMaxWidth={720}
      footer={
        <Typography component="div" sx={FOOTER_SX}>
          <T
            ns="PageSignup"
            keyName="Already have an account? <login>Log in</login>"
            params={{
              login: (
                <Button
                  variant="text"
                  type="button"
                  data-testid="gate-login"
                  onClick={() => {
                    // As on /signup: a login that fails before the page is
                    // left is reported instead of failing silently.
                    auth.login().catch((error: unknown) => {
                      Sentry.captureException(error);
                      enqueueSnackbar(
                        t("Something went wrong. Please try again.", {
                          ns: "Global",
                        }),
                        { variant: "error" }
                      );
                    });
                  }}
                  sx={ONBOARDING_SMALL_TEXT_BUTTON}
                />
              ),
            }}
          />
        </Typography>
      }
    >
      {/* The board centres this block 7px above the middle of the column. */}
      <Box sx={{ pb: "14px" }}>
        {/* Below the split layout the brand panel is only a header, so the
            collage moves above the choice, fitted to the column. */}
        {!isMinTablet && (
          <GateCollage fit="all" sx={{ mx: "auto", mb: "28px" }} />
        )}
        <Typography
          variant="h1"
          sx={{
            mb: "8px",
            textAlign: "center",
            fontSize: { xs: 28, tablet: 32 },
            fontWeight: 500,
            lineHeight: 1.15,
            letterSpacing: "-0.01em",
            color: ONBOARDING_COLORS.heading,
          }}
        >
          {t("How will you use Meister Systems?", { ns: "PageSignup" })}
        </Typography>
        <Typography
          sx={{
            mb: "32px",
            textAlign: "center",
            fontSize: 16,
            lineHeight: 1.45,
            letterSpacing: "normal",
            color: ONBOARDING_COLORS.body,
          }}
        >
          {t("Choose how you want to get started.", { ns: "PageSignup" })}
        </Typography>
        <Stack spacing="14px">
          <RoleCard
            to={{ pathname: "/signup", search: signupSearch("CRAFTSMAN") }}
            testId="signup-craftsman"
            title={t("As a craft business", { ns: "PageSignup" })}
            description={t(
              "Receive enquiries, build quotes from your services and get them signed online.",
              { ns: "PageSignup" }
            )}
            icon={
              <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.4 2.4-2.1-2.1z" />
            }
          />
          <RoleCard
            to={{ pathname: "/signup", search: signupSearch("CLIENT") }}
            testId="signup-homeowner"
            title={t("As a property owner", { ns: "PageSignup" })}
            description={t(
              "Describe your project, get a quote you understand and follow the work from one place.",
              { ns: "PageSignup" }
            )}
            accent
            icon={
              <>
                <path d="M3 11l9-7 9 7" />
                <path d="M5 10v10h14V10" />
                <path d="M10 20v-6h4v6" />
              </>
            }
          />
        </Stack>
        <Typography
          sx={{
            mt: "22px",
            textAlign: "center",
            fontSize: 13,
            lineHeight: 1.45,
            letterSpacing: "normal",
            color: ONBOARDING_COLORS.muted,
          }}
        >
          {t("Invited by a craftsman? Use the link in your invitation.", {
            ns: "PageSignup",
          })}
        </Typography>
      </Box>
    </SignupSplitLayout>
  );
};

interface RoleCardProps {
  to: { pathname: string; search: string };
  testId: string;
  title: string;
  description: string;
  /** The coral variant of the icon tile. */
  accent?: boolean;
  /** Path data of the 24px outline icon. */
  icon: React.ReactNode;
}

/** A role choice: one link (focusable, with a visible focus ring) around icon, text and arrow. */
const RoleCard = ({
  to,
  testId,
  title,
  description,
  accent = false,
  icon,
}: RoleCardProps) => (
  <MuiLink
    component={Link}
    to={to}
    data-testid={testId}
    underline="none"
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "18px",
      p: "22px 24px",
      textAlign: "left",
      color: ONBOARDING_COLORS.heading,
      bgcolor: "common.white",
      border: `1px solid ${ONBOARDING_COLORS.fieldBorder}`,
      borderRadius: "14px",
      transition: "border-color .15s, box-shadow .15s",
      "&:hover": {
        borderColor: ONBOARDING_COLORS.dsNavy,
        boxShadow: "0 8px 24px rgba(0, 30, 100, 0.10)",
      },
      "&:focus-visible": {
        outline: `2px solid ${ONBOARDING_COLORS.dsNavy}`,
        outlineOffset: "2px",
      },
    }}
  >
    <Box
      aria-hidden
      sx={{
        flex: "none",
        width: 44,
        height: 44,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "12px",
        bgcolor: accent ? "#FFF0ED" : ONBOARDING_COLORS.dsTint,
        color: accent
          ? ONBOARDING_COLORS.brandAccent
          : ONBOARDING_COLORS.dsNavy,
      }}
    >
      <OutlineIcon size={24}>{icon}</OutlineIcon>
    </Box>
    <Stack sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        component="span"
        sx={{
          fontSize: 19,
          fontWeight: 500,
          lineHeight: 1.2,
          letterSpacing: "normal",
          color: ONBOARDING_COLORS.heading,
        }}
      >
        {title}
      </Typography>
      <Typography
        component="span"
        sx={{
          fontSize: 14,
          lineHeight: 1.5,
          letterSpacing: "normal",
          color: ONBOARDING_COLORS.body,
        }}
      >
        {description}
      </Typography>
    </Stack>
    <Box
      aria-hidden
      sx={{ flex: "none", display: "flex", color: ONBOARDING_COLORS.dsNavy }}
    >
      <OutlineIcon size={20}>
        <path d="M5 12h14M13 6l6 6-6 6" />
      </OutlineIcon>
    </Box>
  </MuiLink>
);

/** A 24-grid outline icon with the design's 1.75 round stroke. */
const OutlineIcon = ({
  size,
  children,
}: {
  size: number;
  children: React.ReactNode;
}) => (
  <SvgIcon
    aria-hidden="true"
    viewBox="0 0 24 24"
    sx={{
      width: size,
      height: size,
      fill: "none",
      stroke: "currentColor",
      strokeWidth: 1.75,
      strokeLinecap: "round",
      strokeLinejoin: "round",
    }}
  >
    {children}
  </SvgIcon>
);
